import { useApolloClient, useMutation } from '@apollo/client';
import { useStandaloneContextualSegmentsEnabled } from '@openx/components-targeting/Targeting/ContextualSegments/featureFlags';
import type { CreatePackageFormParam, UseCreateForm } from '@openx/types/package/PackageHookTypes';
import { useSnackbar } from 'notistack';
import { useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router';
import {
  CreatePackageDocument,
  type CreatePackageMutationVariables,
} from '../../../graphql/mutation/packages/CreatePackage.generated';
import { trackContextualSegmentsSaved } from '../../targeting/utils/trackContextualSegmentsSaved';
import { transformTargetingForMutation } from './transformTargetingForMutation';

export const useCreateForm: UseCreateForm = () => {
  const { t } = useTranslation();
  const [createPackageMutation, { loading }] = useMutation(CreatePackageDocument, {
    refetchQueries: ['GetPackageList', 'GetOpenxCuratedPackages'],
  });
  const navigate = useNavigate();
  const { enqueueSnackbar } = useSnackbar();
  const apolloClient = useApolloClient();
  const isStandaloneContextualSegments = useStandaloneContextualSegmentsEnabled();

  const createPackage = useCallback(
    ({ diff, setSubmitErrors, historyStateRef, backToDealForm, values, initialValues }: CreatePackageFormParam) => {
      const packageCreateVariables: CreatePackageMutationVariables = {
        package: {
          ...diff,
          urls: diff.urls ? (diff.urls as string[]) : undefined,
          targeting: transformTargetingForMutation(diff.targeting),
        },
      };

      if (packageCreateVariables.package.private_market) {
        packageCreateVariables.package.private_market.discoverable = packageCreateVariables.package.private_market
          .discoverable
          ? '1'
          : '0';
      }

      createPackageMutation({
        onCompleted: response => {
          const pckg = response.createPackage;

          apolloClient.cache.evict({ fieldName: 'packages', id: 'ROOT_QUERY' });
          apolloClient.cache.evict({ fieldName: 'packageById', id: 'ROOT_QUERY' });
          apolloClient.cache.evict({ fieldName: 'targetablePackages', id: 'ROOT_QUERY' });
          apolloClient.cache.evict({ fieldName: 'openxCuratedPackages', id: 'ROOT_QUERY' });

          trackContextualSegmentsSaved({
            entity: 'package',
            isEdit: false,
            isStandaloneContextualSegments,
            targeting: values.targeting,
            initialTargeting: initialValues.targeting,
            fromDealForm: !!historyStateRef.current?.deal,
          });

          if (historyStateRef.current?.deal) {
            backToDealForm(pckg?.uid, values);
            return;
          }

          enqueueSnackbar(
            t('Package "{packageName}" was successfully {actionLabel}', {
              actionLabel: t('created'),
              packageName: response.createPackage?.name,
            }),
            { variant: 'success' }
          );

          navigate(`/inventory/packages/${pckg?.uid}/details`);
        },
        onError: error => {
          const errorMessages = error.graphQLErrors.reduce((prev: string[], curr) => {
            if (curr?.extensions?.errors) {
              prev.push(curr.extensions.errors[0].message);
            }

            return prev;
          }, []);

          enqueueSnackbar(t('Failed to {actionLabel} package', { actionLabel: t('create') }), {
            variant: 'error',
          });

          setSubmitErrors(errorMessages);
        },
        variables: packageCreateVariables,
      });
    },
    [createPackageMutation, enqueueSnackbar, navigate, t, apolloClient, isStandaloneContextualSegments]
  );

  return { createPackage, isCreating: loading };
};
